import {ExclamationTriangleIcon, QuestionMarkCircleIcon} from "@heroicons/react/20/solid";
import {ReactNode} from "react";
import {OtsDensity, useAppDensity} from "../app/AppContext";
import {OtsFormDialog} from "../components/OtsFormDialog";
import {OtsBrand, OtsColorSchemePreference} from "../theme/OtsTheme";

export type ConfirmationDialogTone = "primary" | "danger";

export type ConfirmationDialogProps = {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  /** More than a sentence: what's being confirmed, laid out under the title. */
  children?: ReactNode;
  confirmLabel?: ReactNode;
  confirmingLabel?: ReactNode;
  cancelLabel?: ReactNode;
  tone?: ConfirmationDialogTone;
  icon?: ReactNode;
  isConfirming?: boolean;
  /** Blocks confirming until the caller's input is valid, e.g. enough items are checked. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  /** Tighter spacing and smaller type for dense hosts such as WaterGo. Defaults to the app's density. */
  density?: OtsDensity;
};

/** A themed, reusable confirmation prompt for consequential React actions. */
export const ConfirmationDialog = ({
  open,
  title,
  description,
  children,
  confirmLabel = "Confirm",
  confirmingLabel,
  cancelLabel = "Cancel",
  tone = "primary",
  icon,
  isConfirming = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
  brand,
  colorScheme,
  density,
}: ConfirmationDialogProps) => {
  const compact = useAppDensity(density) === "compact";

  return (
    <OtsFormDialog
      open={open}
      onClose={onCancel}
      onSubmit={onConfirm}
      title={title}
      description={description}
      icon={icon ?? (tone === "danger" ? <ExclamationTriangleIcon/> : <QuestionMarkCircleIcon/>)}
      tone={tone}
      formLabel={typeof title === "string" ? title : "Confirmation"}
      submitLabel={confirmLabel}
      submittingLabel={confirmingLabel}
      progressLabel="Confirmation in progress"
      cancelLabel={cancelLabel}
      submitDisabled={confirmDisabled}
      submitting={isConfirming}
      autoFocusCancel
      size={compact ? "sm" : "md"}
      compact={compact}
      brand={brand}
      colorScheme={colorScheme}>
      {children && <div>{children}</div>}
    </OtsFormDialog>
  );
};
